import React from "react";
import { captureException } from "./sentry";

/**
 * Lightweight fallback ErrorBoundary that forwards to Sentry if available,
 * otherwise just renders the fallback UI. Keeps the root render tree
 * resilient to render/runtime errors.
 */

interface BoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface BoundaryState {
  hasError: boolean;
}

export class SentryErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18 }}>Something went wrong.</h2>
            <p style={{ color: "#64748b", marginTop: 8 }}>
              The error has been reported. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 16,
                padding: "8px 16px",
                background: "#0f766e",
                color: "white",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
